import React, { useRef, useState, useEffect } from 'react';
import PaymentMessage from '../../PaymentSteps/PaymentMessage/PaymentMessage';
import Printer from '../../../../helpers/Printer';
import Activity from '../../../../helpers/Activity';
import defaultPrintMessageImage from '../Signup/images/print-message.png';
import './ShowPrint.css';

function ShowPrint({
    checkoutUrl,
    clientDocumentUrl,
    printMessageImage,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    cancelButtonBackgroundColor,
    cancelButtonBorderColor,
    cancelButtonLabelColor,
    onClose,
    flowFinished,
    metadata
}) {
    const containerRef = useRef(null);
    const [printed, setPrinted] = useState(false);
    const [printError, setPrintError] = useState(false);
    // client_document_url is the client's own printable ticket (e.g. a PDF generated for the sale); checkout_url is only a fallback for clients/flows that don't produce one.
    const documentUrl = clientDocumentUrl || checkoutUrl;

    useEffect(() => {
        if (documentUrl) {
            handlePrint();
            // This is the terminal step of the flow (no retry, no further action besides closing), so mark it finished as soon as there's something to print — closing afterward resets the cart for the next customer, same as the other flows.
            if (flowFinished) flowFinished();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentUrl]);

    const handlePrint = () => {
        if (!documentUrl) return;
        Activity.log(metadata, 'checkout', 'show-print', 'print', true, { documentUrl });
        setPrinted(false);
        setPrintError(false);
        Printer.removePrinterIframe('show-print-iframe');
        // documentUrl comes from the backend, so it's cross-origin from the kiosk's point of view. contentWindow.print() throws a SecurityError on a cross-origin frame, so the document is fetched into a same-origin blob first (same trick Printer already uses for locally-generated PDFs).
        fetch(documentUrl)
            .then(response => response.blob())
            .then(blob => Printer.addPrinterIframe('show-print-iframe', containerRef, URL.createObjectURL(blob)))
            .then(() => setPrinted(true))
            .catch(() => {
                setPrinted(true);
                setPrintError(true);
            })
            .then(() => {
                setTimeout(() => {
                    setPrinted(false);
                    setPrintError(false);
                }, 1000 * 10);
            });
    };

    const handleClose = () => {
        if (onClose) onClose();
    };

    const renderPreview = () => {
        // toolbar=0/navpanes=0 are PDF Open Parameters Chromium's built-in PDF viewer honors on an embedded URL, hiding its toolbar/side panel so only the document itself renders.
        const previewUrl = documentUrl ? `${documentUrl}#toolbar=0&navpanes=0` : documentUrl;
        return <iframe title='show-print-preview' src={previewUrl} className='show-print-preview-iframe'/>;
    };

    return (
        <div ref={containerRef} className='show-print'>
            <PaymentMessage
                isLoading={!documentUrl}
                loaded={!!documentUrl}
                showPreview={true}
                preview={renderPreview()}
                previewSize={{ width: '100%', height: 500 }}
                title1={undefined}
                title2='Tu ticket está listo'
                loadingMessage='Preparando tu ticket...'
                step1Message1='Retira tu ticket'
                step1Message2=' de la impresora para continuar'
                step1Image={printMessageImage || defaultPrintMessageImage}
                showStep2={false}
                step2Message1={undefined}
                step2Message2={undefined}
                step2Image={undefined}
                customStepWidth='100%'
                errorMessage='Se ha producido un error al preparar tu ticket'
                actionButtonBackgroundColor={actionButtonBackgroundColor}
                actionButtonBorderColor={actionButtonBorderColor}
                actionButtonLabelColor={actionButtonLabelColor}
                cancelButtonBackgroundColor={cancelButtonBackgroundColor}
                cancelButtonBorderColor={cancelButtonBorderColor}
                cancelButtonLabelColor={cancelButtonLabelColor}
                onClose={handleClose}
                onSubmit={undefined}
                submitted={printed}
                submitSuccess={!printError}
                submitSuccessMessage='Tu ticket se está imprimiendo...'
                submitErrorMessage='No pudimos imprimir tu ticket. Puedes tomar una foto de esta pantalla para guardar tu turno.'
            />
        </div>
    );
}

export default ShowPrint;
